import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaTimes,
  FaUsersCog,
  FaBuilding,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaShieldAlt,
} from "react-icons/fa";
import apiRequest from "../services/api";

const emptyForm = {
  id: null,
  fullName: "",
  email: "",
  mobile: "",
  rawpassword: "",
  roles: [],
  branchId: null,
  status: "ACTIVE",
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [listError, setListError] = useState("");
  const [formError, setFormError] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedUser, setSelectedUser] = useState(null);

  const [formData, setFormData] = useState(emptyForm);

  const fetchAllData = async () => {
    setIsLoading(true);
    setListError("");

    try {
      const [usersResp, rolesResp, branchesResp] = await Promise.all([
        apiRequest("users"),
        apiRequest("settings/roles"),
        apiRequest("settings/branches"),
      ]);

      setUsers(usersResp.data || []);
      setRoles(rolesResp.data || []);
      setBranches(branchesResp.data || []);
    } catch (err) {
      setListError(
        err?.message || "Failed to synchronize system runtime data."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Helper – always get a reliable numeric ID
  const getBranchId = (obj) => {
    if (!obj) return null;
    const id = obj.branchId ?? obj.id ?? obj.branch?.id;
    return id != null && id !== "" ? Number(id) : null;
  };

  const openModal = (mode, user = null) => {
    setModalMode(mode);
    setSelectedUser(user);
    setFormError("");

    if (user) {
      const activeRoles = (user.roleNames || user.roles || []).map((r) =>
        typeof r === "string" ? { name: r } : { name: r.name, id: r.id }
      );

      setFormData({
        id: user.id,
        fullName: user.fullName || "",
        email: user.email || "",
        mobile: user.mobile || "",
        rawpassword: "",
        roles: activeRoles,
        branchId: getBranchId(user), // ← robust
        status: user.status || "ACTIVE",
      });
    } else {
      setFormData({ ...emptyForm });
    }

    setIsModalOpen(true);
  };

  const handleRoleToggle = (role) => {
    setFormData((prev) => {
      const exists = prev.roles.some((r) => r.name === role.name);

      return {
        ...prev,
        roles: exists
          ? prev.roles.filter((r) => r.name !== role.name)
          : [...prev.roles, { name: role.name, id: role.id }],
      };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError("");

    const endpoint = "users";
    const method = modalMode === "create" ? "POST" : "PUT";

    const payload = {
      id: formData.id,
      fullName: formData.fullName,
      email: formData.email,
      mobile: formData.mobile,
      rawpassword: formData.rawpassword,
      status: formData.status,
      branch: formData.branchId ? { id: formData.branchId } : null,
      roles: formData.roles.map((r) => ({
        id: r.id,
        name: r.name,
      })),
    };

    if (
      modalMode === "edit" &&
      (!payload.rawpassword || payload.rawpassword.trim() === "")
    ) {
      delete payload.rawpassword;
    }

    if (
      modalMode === "create" &&
      (!payload.rawpassword || payload.rawpassword.length < 6)
    ) {
      setFormError("Password is required and must be at least 6 characters.");
      return;
    }

    try {
      await apiRequest(endpoint, {
        method,
        body: payload,
      });

      fetchAllData();
      setIsModalOpen(false);
    } catch (err) {
      setFormError(
        err?.message || "Persistence engine rejected the user payload context."
      );
    }
  };

  const handleDelete = async (id, name) => {
    if (
      !window.confirm(
        `Are you certain you want to change status to DELETED for user "${name}"?`
      )
    )
      return;

    try {
      await apiRequest(`users/${id}`, {
        method: "DELETE",
      });

      fetchAllData();
    } catch (err) {
      setListError(err?.message || "Deactivation sequence failed.");
    }
  };

  const inputClass =
    "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:opacity-60 transition dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-emerald-500/50";

  const selectClass =
    "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:opacity-60 transition appearance-none dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:focus:border-emerald-500/50";

  const labelClass =
    "text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400";

  return (
    <div className="min-h-full p-4 md:p-8 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100 relative pb-24">
      {/* Module Title Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <FaUsersCog className="text-emerald-500" />
            User Directory
          </h1>

          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Manage system users, assign branch and assign role.
          </p>
        </div>

        <button
          onClick={() => openModal("create")}
          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl transition-all shadow-lg shadow-emerald-600/20"
        >
          <FaPlus />
          Create User
        </button>
      </div>

      {/* Exception Banner */}
      {listError && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-medium dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
          {listError}
        </div>
      )}

      {/* Users Table */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[11px] font-bold tracking-wider uppercase dark:bg-slate-950/50 dark:border-white/10 dark:text-slate-400">
                <th className="px-6 py-4">User</th>
                <th className="px-6 py-4">Contacts</th>
                <th className="px-6 py-4">Branch</th>
                <th className="px-6 py-4">Roles</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 text-slate-700 text-xs dark:divide-white/5 dark:text-slate-300">
              {isLoading ? (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center py-8 text-slate-400 dark:text-slate-500"
                  >
                    Querying ...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center py-8 text-slate-400 dark:text-slate-500"
                  >
                    No profiles found.
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr
                    key={u.id}
                    className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="px-6 py-4 font-semibold">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold tracking-wide text-slate-800 dark:text-slate-200">
                          {u.fullName}
                        </span>

                        <span
                          className={`text-[10px] mt-0.5 uppercase tracking-widest font-bold ${
                            u.status === "DELETED"
                              ? "text-red-600 dark:text-red-400"
                              : "text-emerald-600 dark:text-emerald-400"
                          }`}
                        >
                          {u.status || "ACTIVE"}
                        </span>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-slate-500 dark:text-slate-400">
                      <div className="space-y-1">
                        <span className="flex items-center gap-1.5">
                          <FaEnvelope className="text-[10px]" />
                          {u.email}
                        </span>

                        {u.mobile && (
                          <span className="flex items-center gap-1.5 text-slate-400 dark:text-slate-500">
                            <FaPhone className="text-[10px]" />
                            {u.mobile}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      {getBranchId(u) ? (
                        <span className="flex items-center gap-1.5 text-xs font-medium text-blue-700 bg-blue-50 px-2.5 py-1 rounded-lg border border-blue-200 w-fit dark:text-blue-400 dark:bg-blue-500/5 dark:border-blue-500/10">
                          <FaBuilding />
                          {u.branchName || "~NA~"}
                        </span>
                      ) : (
                        <span className="text-slate-400 italic dark:text-slate-600">
                          Headquarters
                        </span>
                      )}
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {u.roleNames && u.roleNames.length > 0 ? (
                          u.roleNames.map((r, i) => (
                            <span
                              key={i}
                              className="bg-slate-100 border border-slate-200 text-emerald-700 font-semibold text-[10px] px-2 py-0.5 rounded-md tracking-wider uppercase dark:bg-slate-950/60 dark:border-white/10 dark:text-emerald-400"
                            >
                              {r}
                            </span>
                          ))
                        ) : (
                          <span className="text-slate-400 italic dark:text-slate-600">
                            No Roles Granted
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openModal("view", u)}
                          className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-600 border border-slate-200 transition dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
                        >
                          <FaEye />
                        </button>

                        <button
                          onClick={() => openModal("edit", u)}
                          className="p-2 bg-blue-50 hover:bg-blue-100 rounded-lg text-blue-600 border border-blue-200 transition dark:bg-blue-600/10 dark:hover:bg-blue-600/20 dark:text-blue-400 dark:border-blue-500/10"
                        >
                          <FaEdit />
                        </button>

                        <button
                          onClick={() => handleDelete(u.id, u.fullName)}
                          className="p-2 bg-red-50 hover:bg-red-100 rounded-lg text-red-600 border border-red-200 transition dark:bg-red-600/10 dark:hover:bg-red-600/20 dark:text-red-400 dark:border-red-500/10"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom Modal Sheet */}
      <div
        className={`fixed inset-0 z-[2000] flex flex-col justify-end transition-opacity duration-300 ${
          isModalOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm dark:bg-slate-950/70"
          onClick={() => setIsModalOpen(false)}
        />

        <div
          className={`relative w-full max-w-4xl mx-auto bg-white border border-slate-200 rounded-t-3xl p-6 sm:p-8 shadow-[0_-15px_40px_rgba(15,23,42,0.15)] dark:bg-slate-900 dark:border-white/10 dark:shadow-[0_-15px_40px_rgba(0,0,0,0.5)] transition-transform duration-300 transform space-y-6 overflow-y-auto max-h-[90vh] ${
            isModalOpen ? "translate-y-0" : "translate-y-full"
          }`}
        >
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white capitalize">
                {modalMode} User Registry
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Manage user identity, access and organizational assignment.
              </p>
            </div>

            <button
              onClick={() => setIsModalOpen(false)}
              className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 transition-colors dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
            >
              <FaTimes className="text-sm" />
            </button>
          </div>

          {formError && (
            <div className="p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs font-medium dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
              {formError}
            </div>
          )}

          <form
            key={selectedUser ? `user-${selectedUser.id}` : "create"}
            onSubmit={handleSave}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>Full Name</label>
                <input
                  type="text"
                  required
                  disabled={modalMode === "view"}
                  value={formData.fullName}
                  onChange={(e) =>
                    setFormData({ ...formData, fullName: e.target.value })
                  }
                  className={inputClass}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>Email Address</label>
                <input
                  type="email"
                  required
                  disabled={modalMode !== "create"}
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:opacity-40 transition dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-emerald-500/50"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>Mobile Number</label>
                <input
                  type="text"
                  disabled={modalMode === "view"}
                  value={formData.mobile}
                  onChange={(e) =>
                    setFormData({ ...formData, mobile: e.target.value })
                  }
                  className={inputClass}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={`${labelClass} flex items-center gap-1`}>
                  <FaLock className="text-[10px]" />
                  Password
                  {modalMode === "edit" && " (Leave blank to preserve hash)"}
                </label>
                <input
                  type="password"
                  required={modalMode === "create"}
                  disabled={modalMode === "view"}
                  value={formData.rawpassword}
                  onChange={(e) =>
                    setFormData({ ...formData, rawpassword: e.target.value })
                  }
                  className={inputClass}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 pt-2">
              {/* Branch Select – now fully robust */}
              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>Assigned Branch</label>

                <select
                  disabled={modalMode === "view"}
                  value={
                    formData.branchId != null ? String(formData.branchId) : ""
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData({
                      ...formData,
                      branchId: val === "" ? null : Number(val),
                    });
                  }}
                  className={selectClass}
                >
                  <option value="">Headquarters</option>

                  {branches.map((b) => {
                    const id = b.id ?? b.branchId; // ← critical fix
                    return (
                      <option key={id} value={String(id)}>
                        {b.name}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Roles */}
              <div className="space-y-2">
                <label className={`${labelClass} flex items-center gap-1`}>
                  <FaShieldAlt className="text-emerald-500" />
                  Role
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {roles.map((roleEntity) => {
                    const isChecked = formData.roles.some(
                      (r) => r.name === roleEntity.name
                    );

                    return (
                      <label
                        key={roleEntity.id}
                        className={`flex items-center gap-3 p-3 rounded-xl border text-xs cursor-pointer select-none transition-all ${
                          isChecked
                            ? "bg-emerald-50 border-emerald-200 text-emerald-700 shadow-sm dark:bg-emerald-600/10 dark:border-emerald-500/30 dark:text-emerald-400 dark:shadow-md dark:shadow-emerald-950/20"
                            : "bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:bg-slate-950/40 dark:border-white/5 dark:text-slate-400 dark:hover:bg-slate-900/60 dark:hover:text-slate-200"
                        } ${
                          modalMode === "view"
                            ? "pointer-events-none opacity-70"
                            : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={modalMode === "view"}
                          onChange={() => handleRoleToggle(roleEntity)}
                          className="accent-emerald-500 rounded border-slate-300 dark:border-slate-700 h-4 w-4"
                        />

                        <div className="flex flex-col">
                          <span className="font-bold tracking-wider text-[11px] uppercase text-slate-700 dark:text-slate-200">
                            {roleEntity.name}
                          </span>

                          {roleEntity.description && (
                            <span className="text-[10px] text-slate-400 dark:text-slate-500 font-normal mt-0.5">
                              {roleEntity.description}
                            </span>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>

                {roles.length === 0 && (
                  <p className="text-xs italic text-slate-400 dark:text-slate-600 pl-1">
                    No Roles Found
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 text-xs font-semibold transition dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
              >
                Close
              </button>

              {modalMode !== "view" && (
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold shadow-md shadow-emerald-600/10 transition"
                >
                  {modalMode === "create"
                    ? "Create Profile Node"
                    : "Commit Record Modifications"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
